import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Button,
  IconButton,
  Stack,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";

const sectionHeaderSx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  mb: 2,
};
const sectionIconSx = { fontSize: 18, color: "#7C3AED" };
const sectionLabelSx = {
  fontSize: 12,
  fontWeight: 700,
  color: "#1E293B",
  letterSpacing: 0.5,
  textTransform: "uppercase",
};
const fieldLabelSx = {
  fontSize: 12,
  fontWeight: 500,
  color: "#64748B",
  mb: 0.75,
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "#F8FAFC",
    borderRadius: 2,
    "& fieldset": { borderColor: "#E2E8F0" },
    "&:hover fieldset": { borderColor: "#CBD5E1" },
    "&.Mui-focused fieldset": { borderColor: "#7C3AED", borderWidth: 1 },
  },
  "& .MuiOutlinedInput-input": {
    fontSize: 14,
    color: "#334155",
    py: 1.5,
    px: 1.5,
    "&::placeholder": { color: "#94A3B8", opacity: 1 },
  },
};

const selectSx = {
  bgcolor: "#F8FAFC",
  borderRadius: 2,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E2E8F0" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#CBD5E1" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#7C3AED",
    borderWidth: 1,
  },
  "& .MuiSelect-select": { fontSize: 14, color: "#334155", py: 1.5, px: 1.5 },
};

const cap = (s) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const AddLeadModal = ({
  open,
  onClose,
  onSave,
  telecallers = [],
  treatments = [],
  sources = [],
  existingLeads = [],
}) => {
  const empty = {
    fullName: "",
    email: "",
    phone: "",
    source: "",
    telecaller: "",
    treatmentTypes: [],
    location: "",
    notes: "",
  };

  const [formData, setFormData] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) {
      setFormData(empty);
      setErrors({});
    }
  }, [open]);

  const sourcesClean = useMemo(
    () =>
      Array.isArray(sources) ? sources.filter((s) => s && s !== "all") : [],
    [sources],
  );

  const treatmentsClean = useMemo(
    () =>
      Array.isArray(treatments)
        ? treatments.filter((t) => t && t !== "all")
        : [],
    [treatments],
  );

  const telecallerClean = useMemo(
    () => (Array.isArray(telecallers) ? telecallers.filter((t) => t?.id) : []),
    [telecallers],
  );

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleTreatmentsChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      treatmentTypes: Array.isArray(value) ? value : [],
    }));
  };

  const handleClose = () => {
    if (saving) return;
    setFormData(empty);
    onClose?.();
  };

  const handleSave = async () => {
    if (saving) return;
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = "Name is required";
    else if (!/^[A-Za-z0-9\s.]+$/.test(formData.fullName.trim())) newErrors.fullName = "Name must contain only letters and numbers";
    else if (existingLeads.some((l) => l.client_name?.trim().toLowerCase() === formData.fullName.trim().toLowerCase())) newErrors.fullName = "Lead with this name already exists";
    if (!formData.phone.trim()) newErrors.phone = "Phone is required";
    else if (!/^[+\d][\d\s-]{7,14}$/.test(formData.phone.trim())) newErrors.phone = "Enter a valid phone number";
    else if (existingLeads.some((l) => l.contact?.replace(/\s|-/g, "") === formData.phone.trim().replace(/\s|-/g, ""))) newErrors.phone = "Lead with this phone number already exists";
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) newErrors.email = "Enter a valid email address";
    if (formData.location.trim() && !/^[A-Za-z\s,.-]+$/.test(formData.location.trim())) newErrors.location = "Location must contain only text";
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }
    setSaving(true);
    try {
      await onSave?.(formData);
      setFormData(empty);
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "";
      const lower = msg.toLowerCase();
      if (lower.includes("phone") || lower.includes("contact")) {
        setErrors((prev) => ({ ...prev, phone: msg }));
      } else if (lower.includes("name")) {
        setErrors((prev) => ({ ...prev, fullName: msg }));
      } else if (msg) {
        setErrors((prev) => ({ ...prev, fullName: msg }));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          maxWidth: 520,
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          pt: 3,
          pb: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 20,
              fontWeight: 700,
              color: "#0F172A",
              lineHeight: 1.3,
            }}
          >
            Add New Lead
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#64748B", mt: 0.5 }}>
            Create a new prospect record in the system
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          sx={{
            color: "#94A3B8",
            p: 0.5,
            "&:hover": { bgcolor: "#F1F5F9", color: "#64748B" },
          }}
        >
          <CloseIcon sx={{ fontSize: 22 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 3, py: 3 }}>
        <Box sx={sectionHeaderSx}>
          <PersonOutlineIcon sx={sectionIconSx} />
          <Typography sx={sectionLabelSx}>BASIC DETAILS</Typography>
        </Box>

        <Box sx={{ mb: 2.5 }}>
          <Typography sx={fieldLabelSx}>Full Name <span style={{ color: "#EF4444" }}>*</span></Typography>
          <TextField
            fullWidth
            placeholder="e.g. John Doe"
            value={formData.fullName}
            onChange={handleChange("fullName")}
            size="small"
            error={!!errors.fullName}
            helperText={errors.fullName}
            sx={inputSx}
          />
        </Box>

        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={fieldLabelSx}>Email Address</Typography>
            <TextField
              fullWidth
              placeholder="john@example.com"
              value={formData.email}
              onChange={handleChange("email")}
              size="small"
              error={!!errors.email}
              helperText={errors.email}
              sx={inputSx}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={fieldLabelSx}>Phone Number <span style={{ color: "#EF4444" }}>*</span></Typography>
            <TextField
              fullWidth
              placeholder="+91 98765 43210"
              value={formData.phone}
              onChange={handleChange("phone")}
              size="small"
              error={!!errors.phone}
              helperText={errors.phone}
              sx={inputSx}
            />
          </Box>
        </Stack>

        <Box sx={sectionHeaderSx}>
          <AutoAwesomeIcon sx={sectionIconSx} />
          <Typography sx={sectionLabelSx}>LEAD SOURCE & ASSIGNMENT</Typography>
        </Box>

        <Box sx={{ mb: 2.5 }}>
          <Typography sx={fieldLabelSx}>Source</Typography>
          <FormControl fullWidth size="small">
            <Select
              value={formData.source}
              onChange={handleChange("source")}
              displayEmpty
              sx={selectSx}
              renderValue={(selected) =>
                selected ? (
                  cap(selected)
                ) : (
                  <span style={{ color: "#94A3B8" }}>Select Source</span>
                )
              }
            >
              {sourcesClean.map((s) => (
                <MenuItem key={s} value={s}>
                  {cap(s)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography sx={fieldLabelSx}>Assignee</Typography>
          <FormControl fullWidth size="small">
            <Select
              value={formData.telecaller}
              onChange={handleChange("telecaller")}
              displayEmpty
              sx={selectSx}
              renderValue={(selected) => {
                if (!selected)
                  return (
                    <span style={{ color: "#94A3B8" }}>Assign Telecaller</span>
                  );
                const picked = telecallerClean.find((t) => t.id === selected);
                return picked?.label || "Assign Telecaller";
              }}
            >
              {telecallerClean.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={sectionHeaderSx}>
          <CardGiftcardIcon sx={sectionIconSx} />
          <Typography sx={sectionLabelSx}>SERVICE DETAILS</Typography>
        </Box>

        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={fieldLabelSx}>Service Type</Typography>
            <FormControl fullWidth size="small">
              <Select
                multiple
                value={formData.treatmentTypes}
                onChange={handleTreatmentsChange}
                displayEmpty
                sx={selectSx}
                renderValue={(selected) => {
                  const arr = Array.isArray(selected) ? selected : [];
                  if (arr.length === 0)
                    return (
                      <span style={{ color: "#94A3B8" }}>Select Treatment</span>
                    );
                  return (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      {arr.map((v) => (
                        <Chip
                          key={v}
                          label={cap(v)}
                          size="small"
                          sx={{ height: 22 }}
                        />
                      ))}
                    </Box>
                  );
                }}
              >
                {treatmentsClean.map((t) => (
                  <MenuItem key={t} value={t}>
                    {cap(t)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography sx={fieldLabelSx}>Location</Typography>
            <TextField
              fullWidth
              placeholder="City, State"
              value={formData.location}
              onChange={handleChange("location")}
              size="small"
              error={!!errors.location}
              helperText={errors.location}
              sx={inputSx}
            />
          </Box>
        </Stack>

        <Box sx={sectionHeaderSx}>
          <FormatListBulletedIcon sx={sectionIconSx} />
          <Typography sx={sectionLabelSx}>ADDITIONAL NOTES</Typography>
        </Box>

        <TextField
          fullWidth
          multiline
          rows={3}
          placeholder="Enter any specific requirements or background information..."
          value={formData.notes}
          onChange={handleChange("notes")}
          sx={{
            ...inputSx,
            "& .MuiOutlinedInput-root": {
              ...inputSx["& .MuiOutlinedInput-root"],
              alignItems: "flex-start",
            },
            "& .MuiOutlinedInput-input": {
              ...inputSx["& .MuiOutlinedInput-input"],
              py: 1.5,
            },
          }}
        />
      </DialogContent>

      <Box
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          borderTop: "1px solid #F1F5F9",
        }}
      >
        <Button
          onClick={handleClose}
          disabled={saving}
          sx={{
            fontWeight: 600,
            fontSize: 14,
            color: "#64748B",
            textTransform: "none",
            px: 3,
            py: 1,
            "&:hover": { bgcolor: "#F8FAFC" },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          variant="contained"
          sx={{
            fontWeight: 600,
            fontSize: 14,
            textTransform: "none",
            px: 3,
            py: 1,
            borderRadius: 2,
            bgcolor: "#7C3AED",
            boxShadow: "0 4px 14px rgba(124, 58, 237, 0.4)",
            "&:hover": {
              bgcolor: "#6D28D9",
              boxShadow: "0 4px 14px rgba(124, 58, 237, 0.5)",
            },
          }}
        >
          Save Lead
        </Button>
      </Box>
    </Dialog>
  );
};

export default AddLeadModal;
